import { useEffect, useRef, useState } from "react";
import {
  DEFAULT_WATTAGE,
  LOAD_TYPE_LABEL,
  computeLoadTotals,
  suggestFuse,
  type PanelRow,
  type RowLoad,
  type RowLoadType,
} from "@electro-planner/domain";

/**
 * Popover för att redigera laster på en rad. Anchor-baserad: positioneras
 * relativt det DOM-element som öppnade popoven. Stänger vid klick utanför
 * eller Esc.
 *
 * Användaren kan:
 *   - Se alla laster på raden som en kompakt lista.
 *   - Lägga till ny last (typ, etikett, antal, W/styck).
 *   - Ta bort eller ändra antal/watt på befintliga laster.
 *   - Slå på/av auto-säkring — när på styrs ampRating av summa last.
 *   - Se totaleffekt + förslagen säkring live.
 */

const LOAD_TYPES: RowLoadType[] = [
  "lamp",
  "socket",
  "motor",
  "heater",
  "stove",
  "ev-charger",
  "water-heater",
  "other",
];

function newLoadId(): string {
  return `load_${Math.random().toString(36).slice(2, 10)}`;
}

export type LoadsEditorProps = {
  row: PanelRow;
  anchor: { top: number; left: number; width: number };
  onClose: () => void;
  onChange: (loads: ReadonlyArray<RowLoad>) => void;
  onToggleAuto: (fuseAuto: boolean) => void;
};

export function LoadsEditor({
  row,
  anchor,
  onClose,
  onChange,
  onToggleAuto,
}: LoadsEditorProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const loads = row.loads ?? [];

  // Stäng vid klick utanför eller Escape.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    // setTimeout så samma klick som öppnade popoven inte stänger den.
    const id = window.setTimeout(() => {
      document.addEventListener("mousedown", onDocClick);
    }, 0);
    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const update = (id: string, patch: Partial<RowLoad>) => {
    onChange(loads.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };

  const remove = (id: string) => {
    onChange(loads.filter((l) => l.id !== id));
  };

  const add = (type: RowLoadType) => {
    const load: RowLoad = {
      id: newLoadId(),
      type,
      quantity: 1,
      wattage: DEFAULT_WATTAGE[type],
    };
    onChange([...loads, load]);
  };

  const polesIn: 1 | 3 = row.poles === 3 || row.poles === 4 ? 3 : 1;
  const totals = computeLoadTotals(loads, { poles: polesIn });
  const suggestion = suggestFuse(loads, { poles: polesIn });

  // Håll popoven inom viewport.
  const maxLeft =
    typeof window !== "undefined" ? window.innerWidth - 420 - 16 : 800;
  const left = Math.max(16, Math.min(anchor.left, maxLeft));
  const top = anchor.top + 4;

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={`Laster för ${row.designation}`}
      className="fixed z-50 w-[420px] rounded-lg border border-slate-300 bg-white shadow-xl"
      style={{ top, left }}
    >
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
        <div className="text-sm font-semibold text-slate-800">
          Laster för <span className="font-mono">{row.designation || "(namnlös)"}</span>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-slate-700">
          <input
            type="checkbox"
            checked={row.fuseAuto ?? false}
            onChange={(e) => onToggleAuto(e.target.checked)}
            className="cursor-pointer"
          />
          Auto-säkring
        </label>
      </div>

      <div className="max-h-72 overflow-auto px-3 py-2">
        {loads.length === 0 ? (
          <div className="py-2 text-center text-xs text-slate-500">
            Inga laster än. Lägg till via knapparna nedan.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {loads.map((l) => (
              <li key={l.id} className="flex items-center gap-1.5 text-sm">
                <select
                  value={l.type}
                  onChange={(e) =>
                    update(l.id, { type: e.target.value as RowLoadType })
                  }
                  className="rounded border border-slate-300 bg-white px-1 py-0.5 text-xs"
                  aria-label="Lasttyp"
                >
                  {LOAD_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {LOAD_TYPE_LABEL[t]}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min={1}
                  value={l.quantity ?? 1}
                  onChange={(e) =>
                    update(l.id, {
                      quantity: parseInt(e.target.value, 10) || 1,
                    })
                  }
                  className="w-14 rounded border border-slate-300 px-1 py-0.5 text-xs"
                  aria-label="Antal"
                  title="Antal"
                />
                <span className="text-xs text-slate-400">×</span>
                <input
                  type="number"
                  min={0}
                  value={l.wattage ?? DEFAULT_WATTAGE[l.type]}
                  onChange={(e) =>
                    update(l.id, { wattage: parseInt(e.target.value, 10) || 0 })
                  }
                  className="w-20 rounded border border-slate-300 px-1 py-0.5 text-xs"
                  aria-label="Effekt per styck"
                  title="W per styck"
                />
                <span className="text-xs text-slate-400">W</span>
                <input
                  type="text"
                  value={l.label ?? ""}
                  onChange={(e) => update(l.id, { label: e.target.value || undefined })}
                  placeholder="Anteckning"
                  className="min-w-0 flex-1 rounded border border-slate-300 px-1 py-0.5 text-xs"
                  aria-label="Etikett"
                />
                <button
                  type="button"
                  onClick={() => remove(l.id)}
                  className="rounded px-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                  title="Ta bort last"
                  aria-label="Ta bort last"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex flex-wrap gap-1 border-t border-slate-200 bg-slate-50 px-3 py-2">
        {LOAD_TYPES.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => add(t)}
            className="rounded border border-slate-300 bg-white px-2 py-0.5 text-xs text-slate-700 hover:bg-sky-50 hover:text-sky-700"
            title={`Lägg till ${LOAD_TYPE_LABEL[t].toLowerCase()}`}
          >
            + {LOAD_TYPE_LABEL[t]}
          </button>
        ))}
      </div>

      <div className="border-t border-slate-200 px-3 py-2 text-xs">
        <div className="flex items-center justify-between">
          <div className="text-slate-600">
            Total last:{" "}
            <strong>{Math.round(totals.watts)} W</strong>
            {" · "}
            <strong>{totals.amps.toFixed(1)} A</strong>
            {polesIn === 3 ? " (3-fas 400 V)" : " (1-fas 230 V)"}
          </div>
          {suggestion ? (
            <div className="font-medium text-sky-800">
              Förslag:{" "}
              <span className="rounded bg-sky-100 px-1.5 py-0.5 font-mono">
                MCB {suggestion.poles}P {suggestion.characteristic}
                {suggestion.ampRating}
              </span>
            </div>
          ) : null}
        </div>
        {row.fuseAuto && suggestion ? (
          <div className="mt-1 text-slate-500">
            Auto-säkring på — ampRating/karakteristik uppdateras automatiskt när
            laster ändras.
          </div>
        ) : row.fuseAuto ? (
          <div className="mt-1 text-slate-500">
            Auto-säkring på — lägg till minst en last för att få förslag.
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Kompakt sammanfattning av laster för visning i tabellcellen. Visar typer
 * grupperade ("3 × Ljuspunkt, 2 × Uttag") med total watt sist.
 */
export function LoadsSummary({ loads }: { loads: ReadonlyArray<RowLoad> }) {
  if (loads.length === 0) {
    return <span className="text-xs text-slate-400">—</span>;
  }
  const byType = new Map<RowLoadType, number>();
  let watts = 0;
  for (const l of loads) {
    const q = l.quantity ?? 1;
    byType.set(l.type, (byType.get(l.type) ?? 0) + q);
    watts += (l.wattage ?? DEFAULT_WATTAGE[l.type]) * q;
  }
  const parts = Array.from(byType.entries()).map(
    ([type, q]) => `${q} × ${LOAD_TYPE_LABEL[type].toLowerCase()}`,
  );
  return (
    <span
      className="text-xs text-slate-700"
      title={`${parts.join(", ")} · totalt ${Math.round(watts)} W`}
    >
      {parts.join(", ")}
    </span>
  );
}

// State + open-helper delas via custom hook.
export type LoadsEditorAnchor = { top: number; left: number; width: number };

export function useLoadsEditorState(): {
  openFor: PanelRow | null;
  anchor: LoadsEditorAnchor | null;
  open: (row: PanelRow, el: HTMLElement) => void;
  close: () => void;
} {
  const [openFor, setOpenFor] = useState<PanelRow | null>(null);
  const [anchor, setAnchor] = useState<LoadsEditorAnchor | null>(null);
  return {
    openFor,
    anchor,
    open: (row, el) => {
      const rect = el.getBoundingClientRect();
      setAnchor({
        top: rect.bottom,
        left: rect.left,
        width: Math.max(320, rect.width),
      });
      setOpenFor(row);
    },
    close: () => {
      setOpenFor(null);
      setAnchor(null);
    },
  };
}
